import BarChart from "../chart/BarChart";
import LineChart from "../chart/LineChart";
import {useEffect, useState} from "react";
import {
    getSalesChartData,
    getCustomersChartData,
    getAverageTicketChartData,
    getProductSaleValueChartData,
    getProductSaleCountChartData,
    getProductSaleAverageTicketChartData,
    getServiceValueChartData,
    getServiceCountChartData,
    getServiceAverageTicketChartData,
    getAutomotiveServiceValueChartData,
    getAutomotiveServiceCountChartData,
    getAutomotiveServiceAverageTicketChartData,
    getCombinedValueChartData,
    getCombinedCountChartData,
    getCombinedTicketChartData,
    getDashboardPermissions,
} from "../../utils/requestUtils";
import {getYearMonthFormat, parseLocalDate} from "../../utils/utils";
import {useHomeShortcuts} from "../home/useHomeShortcuts";
import HomeShortcuts from "../home/HomeShortcuts";

function useDashboardPermissions() {
    const [permissions, setPermissions] = useState(null);

    useEffect(() => {
        getDashboardPermissions().then(perms => setPermissions(perms || []));
    }, []);

    const has = (name) => permissions !== null && permissions.includes(name);
    return {permissions, has};
}

function useChartData(fetcher, startDate, endDate) {
    const [labels, setLabels] = useState([]);
    const [values, setValues] = useState([]);
    const [avgValues, setAvgValues] = useState([]);

    useEffect(() => {
        if (!fetcher) return;
        fetcher(startDate, endDate).then(data => {
            if (!data || data.length === 0) return;
            setLabels(data.map(d => getYearMonthFormat(parseLocalDate(d.date))));
            const vals = data.map(d => d.value);
            setValues(vals);
            const avg = vals.reduce((s, v) => s + v, 0) / vals.length;
            setAvgValues(vals.map(() => avg));
        });
    }, [fetcher]);

    return {labels, values, avgValues};
}

function flattenCombined(data) {
    if (!data) return [];
    const map = {};
    const merge = (arr) => (arr || []).forEach(d => {
        const date = parseLocalDate(d.date);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!map[key]) map[key] = {date: d.date, value: 0};
        map[key].value += d.value;
    });
    merge(data.rental);
    merge(data.sale);
    return Object.keys(map).sort().map(k => map[k]);
}

async function getCombinedValueFlatChartData(startDate, endDate) {
    return flattenCombined(await getCombinedValueChartData(startDate, endDate));
}

async function getCombinedCountFlatChartData(startDate, endDate) {
    return flattenCombined(await getCombinedCountChartData(startDate, endDate));
}

const ChartBlock = ({title, children}) => (
    <div style={{marginTop: '10px'}}>
        <div className="header-label">{title}</div>
        {children}
    </div>
);

const DashBoardPage = () => {
    const {permissions, has} = useDashboardPermissions();
    const {items: homeShortcuts} = useHomeShortcuts();

    const currentDate = new Date();
    const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 12, 1);
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

    const showCombinedValue = has('VIEW_CHART_COMBINED_VALUE');
    const showCombinedCount = has('VIEW_CHART_COMBINED_COUNT');
    const showCombinedTicket = has('VIEW_CHART_COMBINED_TICKET');

    const showRentalValue = has('VIEW_CHART_RENTAL_VALUE');
    const showRentalCount = has('VIEW_CHART_RENTAL_COUNT');
    const showRentalTicket = has('VIEW_CHART_RENTAL_TICKET');

    const showProductSaleValue = has('VIEW_CHART_PRODUCT_SALE_VALUE');
    const showProductSaleCount = has('VIEW_CHART_PRODUCT_SALE_COUNT');
    const showProductSaleTicket = has('VIEW_CHART_PRODUCT_SALE_TICKET');

    const showServiceValue = has('VIEW_CHART_SERVICE_VALUE');
    const showServiceCount = has('VIEW_CHART_SERVICE_COUNT');
    const showServiceTicket = has('VIEW_CHART_SERVICE_TICKET');

    const showAutomotiveValue = has('VIEW_CHART_AUTOMOTIVE_VALUE');
    const showAutomotiveCount = has('VIEW_CHART_AUTOMOTIVE_COUNT');
    const showAutomotiveTicket = has('VIEW_CHART_AUTOMOTIVE_TICKET');

    const rentalValue = useChartData(showRentalValue ? getSalesChartData : null, startDate, endDate);
    const rentalCount = useChartData(showRentalCount ? getCustomersChartData : null, startDate, endDate);
    const rentalTicket = useChartData(showRentalTicket ? getAverageTicketChartData : null, startDate, endDate);

    const productSaleValue = useChartData(showProductSaleValue ? getProductSaleValueChartData : null, startDate, endDate);
    const productSaleCount = useChartData(showProductSaleCount ? getProductSaleCountChartData : null, startDate, endDate);
    const productSaleTicket = useChartData(showProductSaleTicket ? getProductSaleAverageTicketChartData : null, startDate, endDate);

    const serviceValue = useChartData(showServiceValue ? getServiceValueChartData : null, startDate, endDate);
    const serviceCount = useChartData(showServiceCount ? getServiceCountChartData : null, startDate, endDate);
    const serviceTicket = useChartData(showServiceTicket ? getServiceAverageTicketChartData : null, startDate, endDate);

    const automotiveValue = useChartData(showAutomotiveValue ? getAutomotiveServiceValueChartData : null, startDate, endDate);
    const automotiveCount = useChartData(showAutomotiveCount ? getAutomotiveServiceCountChartData : null, startDate, endDate);
    const automotiveTicket = useChartData(showAutomotiveTicket ? getAutomotiveServiceAverageTicketChartData : null, startDate, endDate);

    const combinedValue = useChartData(showCombinedValue ? getCombinedValueFlatChartData : null, startDate, endDate);
    const combinedCount = useChartData(showCombinedCount ? getCombinedCountFlatChartData : null, startDate, endDate);
    const combinedTicket = useChartData(showCombinedTicket ? getCombinedTicketChartData : null, startDate, endDate);

    if (permissions === null) return null;

    if (homeShortcuts.length > 0) {
        return <HomeShortcuts items={homeShortcuts} />;
    }

    const hasAny = showRentalValue || showRentalCount || showRentalTicket ||
        showProductSaleValue || showProductSaleCount || showProductSaleTicket ||
        showServiceValue || showServiceCount || showServiceTicket ||
        showAutomotiveValue || showAutomotiveCount || showAutomotiveTicket ||
        showCombinedValue || showCombinedCount || showCombinedTicket;

    if (!hasAny) return null;

    return (
        <div className="dashboard-wrapper">
            {showRentalValue && (
                <ChartBlock title="Evolução do Faturamento - Aluguel">
                    <BarChart labels={rentalValue.labels} dataValues={rentalValue.values} currencyformat color={"#36A2EB66"}/>
                </ChartBlock>
            )}
            {showRentalCount && (
                <ChartBlock title="Evolução dos Aluguéis - Aluguel">
                    <BarChart labels={rentalCount.labels} dataValues={rentalCount.values} color={"#FF9F5E66"}/>
                </ChartBlock>
            )}
            {showRentalTicket && (
                <ChartBlock title="Evolução do Ticket Médio - Aluguel">
                    <LineChart labels={rentalTicket.labels} dataValues={rentalTicket.values} secondaryDataValues={rentalTicket.avgValues} label1="Ticket médio" label2="Média" currencyformat/>
                </ChartBlock>
            )}
            {showProductSaleValue && (
                <ChartBlock title="Evolução do Faturamento - Venda">
                    <BarChart labels={productSaleValue.labels} dataValues={productSaleValue.values} currencyformat color={"#36A2EB66"}/>
                </ChartBlock>
            )}
            {showProductSaleCount && (
                <ChartBlock title="Evolução das Vendas - Venda">
                    <BarChart labels={productSaleCount.labels} dataValues={productSaleCount.values} color={"#FF9F5E66"}/>
                </ChartBlock>
            )}
            {showProductSaleTicket && (
                <ChartBlock title="Evolução do Ticket Médio - Venda">
                    <LineChart labels={productSaleTicket.labels} dataValues={productSaleTicket.values} secondaryDataValues={productSaleTicket.avgValues} label1="Ticket médio" label2="Média" currencyformat/>
                </ChartBlock>
            )}
            {showServiceValue && (
                <ChartBlock title="Evolução do Faturamento - Serviço">
                    <BarChart labels={serviceValue.labels} dataValues={serviceValue.values} currencyformat color={"#36A2EB66"}/>
                </ChartBlock>
            )}
            {showServiceCount && (
                <ChartBlock title="Evolução dos Serviços - Serviço">
                    <BarChart labels={serviceCount.labels} dataValues={serviceCount.values} color={"#FF9F5E66"}/>
                </ChartBlock>
            )}
            {showServiceTicket && (
                <ChartBlock title="Evolução do Ticket Médio - Serviço">
                    <LineChart labels={serviceTicket.labels} dataValues={serviceTicket.values} secondaryDataValues={serviceTicket.avgValues} label1="Ticket médio" label2="Média" currencyformat/>
                </ChartBlock>
            )}
            {showAutomotiveValue && (
                <ChartBlock title="Evolução do Faturamento - Serviço Automotivo">
                    <BarChart labels={automotiveValue.labels} dataValues={automotiveValue.values} currencyformat color={"#36A2EB66"}/>
                </ChartBlock>
            )}
            {showAutomotiveCount && (
                <ChartBlock title="Evolução dos Serviços Automotivos - Serviço Automotivo">
                    <BarChart labels={automotiveCount.labels} dataValues={automotiveCount.values} color={"#FF9F5E66"}/>
                </ChartBlock>
            )}
            {showAutomotiveTicket && (
                <ChartBlock title="Evolução do Ticket Médio - Serviço Automotivo">
                    <LineChart labels={automotiveTicket.labels} dataValues={automotiveTicket.values} secondaryDataValues={automotiveTicket.avgValues} label1="Ticket médio" label2="Média" currencyformat/>
                </ChartBlock>
            )}
            {showCombinedValue && (
                <ChartBlock title="Faturamento - Aluguéis + Vendas">
                    <BarChart labels={combinedValue.labels} dataValues={combinedValue.values} currencyformat color={"#36A2EB66"}/>
                </ChartBlock>
            )}
            {showCombinedCount && (
                <ChartBlock title="Clientes - Aluguéis + Vendas">
                    <BarChart labels={combinedCount.labels} dataValues={combinedCount.values} color={"#FF9F5E66"}/>
                </ChartBlock>
            )}
            {showCombinedTicket && (
                <ChartBlock title="Ticket Médio - Aluguéis e Vendas">
                    <LineChart labels={combinedTicket.labels} dataValues={combinedTicket.values} secondaryDataValues={combinedTicket.avgValues} label1="Ticket médio" label2="Média" currencyformat/>
                </ChartBlock>
            )}
        </div>
    );
};

export default DashBoardPage;
